<template>
  <transition name="card-expand" @after-leave="$emit('transition-end')">
    <div 
      v-if="course" 
      class="expanded-course-overlay"
      @click.self="$emit('close')"
    >
      <div 
        class="expanded-course" 
        :class="{ 'animation-active': animationActive }"
        :style="clickPosition ? `--origin-left: ${clickPosition.left}px; --origin-top: ${clickPosition.top}px; --origin-width: ${clickPosition.width}px; --origin-height: ${clickPosition.height}px;` : ''"
      >
        <div class="expanded-header">

          <button class="back-btn" @click="$emit('close')"><svg t="1747387769340" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4602" width="200" height="200"><path d="M374.2208 515.9424L774.912 115.2512a56.448 56.448 0 1 0-79.8464-79.8464L255.8976 474.5984c-22.016 22.016-22.016 57.7792 0 79.8208 1.7408 1.7408 3.712 3.1232 5.632 4.608 0.3072 0.3328 0.5632 0.768 0.896 1.1008l428.8768 428.8768a55.1168 55.1168 0 1 0 77.952-77.952L374.2208 515.9424z" fill="#F8B62D" p-id="4603"></path></svg> <span>返回</span></button>
        </div>
        
        <div class="expanded-content">
          <!-- 课程详细信息 -->
          <div class="course-info">
            <!-- 大图显示 -->
            <div 
              class="course-image-large" 
              :style="course.imgUrl ? {
                backgroundImage: `url(${course.imgUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {
                backgroundImage: 'linear-gradient(45deg, #f8b62d 25%, #ffcd5d 25%, #ffcd5d 50%, #f8b62d 50%, #f8b62d 75%, #ffcd5d 75%, #ffcd5d 100%)',
                backgroundSize: '20px 20px'
              }"
            ></div>

            <div class="course-details">
              <h2 class="expanded-title">{{ course.title }}</h2>
              <p class="course-description-full">{{ course.description }}</p>
              <div class="course-meta-full">
                <span>讲师: {{ course.author }}</span>
                <span>发布时间: {{ formatDate(course.time) }}</span>
              </div>
            </div>
          </div>
          
          <!-- 课程资源 -->
          <div class="course-resources">
            <div class="resource-tabs">
              <button 
                class="tab-btn" 
                :class="{ active: activeTab === 'homework' }"
                @click="$emit('update:activeTab', 'homework')"
              >
                作业
              </button>
              <button 
                class="tab-btn" 
                :class="{ active: activeTab === 'exam' }"
                @click="$emit('update:activeTab', 'exam')"
              >
                考试
              </button>
              <button 
                class="tab-btn" 
                :class="{ active: activeTab === 'test' }"
                @click="$emit('update:activeTab', 'test')"
              >
                测试
              </button>
            </div>
            
            <div class="tab-content">
              <!-- 加载状态 -->
              <div v-if="loading" class="loading-container">
                <div class="loader-wrapper">
                  <div class="spinner"></div>
                  <p class="loading-text">正在加载试卷数据...</p>
                </div>
              </div>
              
              <!-- 资源内容 -->
              <template v-else>
                <!-- 作业资源项 -->
                <div v-if="activeTab === 'homework'" class="resource-list">
                  <div 
                    v-for="(item, index) in homeworkResources" 
                    :key="'hw-'+index" 
                    class="resource-item"
                    @click="$emit('resource-click', item)"
                  >
                    <span class="resource-title">{{ item.title }}</span>
                    <span class="resource-date">{{ item.date }}</span>
                  </div>
                  <div v-if="homeworkResources.length === 0" class="empty-resources">
                    暂无作业资源
                  </div>
                </div>
                
                <!-- 测试资源项 -->
                <div v-if="activeTab === 'test'" class="resource-list">
                  <div 
                    v-for="(item, index) in testResources" 
                    :key="'test-'+index" 
                    class="resource-item"
                    @click="$emit('resource-click', item)"
                  >
                    <span class="resource-title">{{ item.title }}</span>
                    <span class="resource-date">{{ item.date }}</span>
                  </div>
                  <div v-if="testResources.length === 0" class="empty-resources">
                    暂无测试资源
                  </div>
                </div>
                
                <!-- 考试资源项 -->
                <div v-if="activeTab === 'exam'" class="resource-list">
                  <div 
                    v-for="(item, index) in examResources" 
                    :key="'ex-'+index" 
                    class="resource-item"
                    @click="$emit('resource-click', item)"
                  >
                    <span class="resource-title">{{ item.title }}</span>
                    <span class="resource-date">{{ item.date }}</span>
                  </div>
                  <div v-if="examResources.length === 0" class="empty-resources">
                    暂无考试资源
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CourseDetailModal',
  props: {
    course: {
      type: Object,
      default: null
    },
    clickPosition: {
      type: Object,
      default: null
    },
    animationActive: {
      type: Boolean,
      default: false
    },
    homeworkResources: {
      type: Array,
      default: () => []
    },
    examResources: {
      type: Array,
      default: () => []
    },
    testResources: {
      type: Array,
      default: () => []
    },
    activeTab: {
      type: String,
      default: 'homework'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'transition-end', 'resource-click', 'update:activeTab'],
  methods: {
    formatDate(dateString) {
      if (!dateString) return '未知日期';
      
      // 如果日期包含T，说明是ISO格式，需要分割
      if (dateString.includes('T')) {
        dateString = dateString.split('T')[0];
      }
      
      // 将YYYY-MM-DD格式转换为YYYY年MM月DD日
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[0]}年${parts[1]}月${parts[2]}日`;
      }
      
      return dateString;
    }
  },
  watch: {
    activeTab(newVal) {
      this.$emit('update:activeTab', newVal);
    }
  }
}
</script>

<style scoped>
/* 卡片展开动画样式 */
.expanded-course-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000000; /* 修改为与其他模态框相同的高z-index */
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
}

/* 其他样式保持不变 */

.expanded-course {
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  min-height: 90vh;
  overflow: hidden; /* 改为 hidden，防止整体滚动 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  background-color: white;
  border: 1.8px solid rgb(184 184 184);
  border-radius: 32px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  display: flex;  /* 添加 flex 布局 */
  flex-direction: column;  /* 垂直方向排列 */
}

.expanded-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;  /* 添加左右内边距 */
  flex-shrink: 0;  /* 防止头部压缩 */
}

.expanded-content {
  flex: 1;  /* 占据剩余空间 */
  overflow-y: auto;  /* 只在内容区域启用滚动 */
  padding: 0 20px 20px;  /* 添加内边距，右侧20px抵消滚动条宽度 */
}

/* 移除原有的左右内边距 */
.expanded-course {
  padding-left: 0;
  padding-right: 0;
}

/* 确保滚动条样式在内容区域生效 */
.expanded-content::-webkit-scrollbar {
  display: none;
}

/* 卡片展开动画 */
.card-expand-enter-active,
.card-expand-leave-active {
  transition: opacity 0.35s ease-out;
}

.card-expand-enter-from,
.card-expand-leave-to {
  opacity: 0;
}

/* 为内容卡片添加独立的动画 */
.card-expand-enter-active .expanded-course {
  transition: opacity 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.35s cubic-bezier(0.25, 0.7, 0.8, 0.94);
}

.card-expand-leave-active .expanded-course {
  transition: opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.card-expand-enter-from .expanded-course,
.card-expand-leave-to .expanded-course {
  transform: translateY(30px);
}

.expanded-course.animation-active {
  animation: expand-card 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes expand-card {
  0% {
    transform: translateY(40px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 课程详情样式 */
.expanded-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.expanded-title {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.back-btn {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background-color: transparent;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 20px;
  margin-bottom: 10px;
}

.back-btn svg {
  fill: #F8B62D;
  width: 25px;
  height: 25px;
}
.back-btn span {
  color: #F8B62D;
  margin-left: 5px;
  font-size: 20px;
}





.course-info {
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-bottom: 30px;
}


  .course-image-large {
    width: 300px;
    height: 200px;
  }
.course-details {
  flex: 1;
}

.course-description-full {
  margin-top: 0;
  margin-bottom: 16px;
  line-height: 1.6;
  color: #555;
}

.course-meta-full {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #666;
  font-size: 14px;
}

/* 资源标签页样式 */
.resource-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
}

.tab-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  position: relative;
  transition: all 0.3s;
}

.tab-btn.active {
  color: #f8b62d;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #f8b62d;
}

.tab-content {
  min-height: 200px;
}

.resource-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.resource-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid transparent; /* 添加透明边框 */
  transition: all 0.3s ease; /* 修改过渡效果包含所有属性 */
  cursor: pointer;
}

.resource-item:hover {
  /* background-color: #f0f0f0; */
  border-color: #f8b62d; /* 悬停时显示黄色边框 */
  transform: translateY(-2px); /* 添加轻微位移效果 */
  box-shadow: 0 2px 8px rgba(255, 179, 1, 0.05); /* 添加轻微阴影 */
}

.resource-item:hover span {
  color: #f8b62d; /* 悬停时改变标题颜色 */;
}

.resource-title {
  font-weight: 500;
  color: #333;
}

.resource-date {
  color: #999;
  font-size: 14px;
}

.empty-resources {
  text-align: center;
  padding: 30px 0;
  color: #999;
  font-style: italic;
}

.course-resources {
  margin-top: 10px;
  margin-bottom: 20px;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 40px 0;
}

.loader-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f8b62d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin: 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}

@media (max-width: 768px) {
  .back-btn{
    margin-top: 15px;
  }
  .course-info {
    flex-direction: column;
  }
  
  
.course-image-large {
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
}

  .expanded-course{
    height: 85vh;
    min-height: 85vh;
    margin-left: 10px;
    margin-right: 10px;
    padding-top: 10px;
    /* border-radius: 0px */
  }
  .expanded-content{
    gap: 0px;
  }
  .resource-date{
    min-width: 90px;
  }
}


</style>